import React, { useEffect, useState } from "react";
import "./CartPage.css";
import { useCart } from "../context/CartContext";
import CheckoutModal from "./CheckoutModal";

export default function CartPage({ onBack, onEditItem, canEditItem }) {
  const {
    items,
    itemCount,
    subtotal,
    taxTotal,
    grandTotal,
    updateQuantity,
    clearCart,
    syncError,
    orderContext,
    setOrderNotes,
    invalidCartItems,
    cartAvailabilityError,
  } = useCart();

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const invalidItemCount = Object.keys(invalidCartItems || {}).length;
  const hasInvalidItems = invalidItemCount > 0;

  // Lock background scroll while the cart page is open.
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  return (
    <div className="cart-page">
      {/* Sticky header */}
      <div className="cart-page-header">
        <button className="cart-page-back" onClick={onBack} aria-label="Back to menu">
          ←
        </button>
        <h1>Your Cart</h1>
        <span className="cart-page-count">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
      </div>

      {/* Sync notice */}
      {syncError && (
        <div className="cart-sync-note">
          Cart is saved on this device. Live sync is temporarily unavailable.
        </div>
      )}

      {(hasInvalidItems || cartAvailabilityError) && (
        <div className="cart-availability-note">
          {hasInvalidItems
            ? "Some items are now sold out. Remove the highlighted items to continue."
            : cartAvailabilityError}
        </div>
      )}

      {/* Scrollable items */}
      <div className="cart-page-items">
        {items.length === 0 ? (
          <div className="cart-empty">
            <div className="cart-empty-icon">🧋</div>
            <p>Your cart is empty</p>
            <button className="cart-empty-btn" onClick={onBack}>
              Browse Menu
            </button>
          </div>
        ) : (
          items.map((item) => {
            const showEdit = Boolean(onEditItem && (!canEditItem || canEditItem(item)));
            const invalidItem = invalidCartItems?.[item.id];
            const invalidNames = invalidItem?.matches
              ?.map((match) => match.name || match.soldOutName)
              .filter(Boolean)
              .join(", ");

            return (
            <div className={`cart-row ${invalidItem ? "cart-row-invalid" : ""}`} key={item.id}>
              {item.imageUrl && (
                <img className="cart-row-img" src={item.imageUrl} alt={item.name} />
              )}
              <div className="cart-row-main">
                <div className="cart-row-info">
                  <p className="cart-row-name">{item.name}</p>
                  {item.selectedOptions?.length > 0 && (
                    <p className="cart-row-options">
                      {item.selectedOptions
                        .map((o) => (Number(o?.price) > 0 ? `${o.title} (+₹${Math.round(o.price)})` : o.title))
                        .join(", ")}
                    </p>
                  )}
                  {invalidItem && (
                    <p className="cart-row-sold-out">
                      Sold out{invalidNames ? `: ${invalidNames}` : ""}
                    </p>
                  )}
                  {showEdit && (
                    <button
                      className="cart-row-edit"
                      onClick={() => onEditItem(item)}
                      aria-label={`Edit ${item.name}`}
                    >
                      Edit <span aria-hidden="true">›</span>
                    </button>
                  )}
                </div>
                <div className="cart-row-side">
                  <div className="modal-sticky-qty-controls cart-row-qty-controls">
                    <button
                      className="modal-sticky-qty-btn"
                      onClick={() => updateQuantity(item.id, -1)}
                      aria-label={
                        item.quantity <= 1 ? `Remove ${item.name}` : `Remove one ${item.name}`
                      }
                    >
                      −
                    </button>
                    <span className="modal-sticky-qty-value">{item.quantity}</span>
                    <button
                      className="modal-sticky-qty-btn"
                      onClick={() => updateQuantity(item.id, 1)}
                      aria-label={`Add one ${item.name}`}
                    >
                      +
                    </button>
                  </div>
                  <strong className="cart-row-line">₹{Math.round(item.unitPrice * item.quantity)}</strong>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Sticky footer with totals + checkout */}
      {items.length > 0 && (
        <div className="cart-page-footer">
          <label className="cart-order-notes">
            <span>Special instructions</span>
            <textarea
              value={orderContext?.notes || ""}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Sugar preference, allergies, etc."
              rows={2}
              maxLength={250}
            />
          </label>

          <div className="cart-totals">
            <div className="cart-totals-row">
              <span>Subtotal</span>
              <span>₹{Math.round(subtotal)}</span>
            </div>
            <div className="cart-totals-row">
              <span>Taxes</span>
              <span>₹{Math.round(taxTotal)}</span>
            </div>
            <div className="cart-totals-row cart-totals-grand">
              <span>Total</span>
              <strong>₹{Math.round(grandTotal)}</strong>
            </div>
          </div>

          <div className="cart-page-actions">
            <button className="cart-clear" onClick={clearCart}>
              Clear
            </button>
            <button
              className="cart-checkout"
              onClick={() => setCheckoutOpen(true)}
              disabled={hasInvalidItems}
            >
              {hasInvalidItems ? "Remove sold out items" : `Checkout · ₹${Math.round(grandTotal)}`}
            </button>
          </div>
        </div>
      )}

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={() => {
          setCheckoutOpen(false);
          onBack();
        }}
      />
    </div>
  );
}
